import { QrCode, Wifi } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from '@/shared/hooks/useCopyToClipboard';
import { CopyIconButton } from '../ui';

export const ACCESS_TILE = 'rounded-guest-card border border-white/15 bg-white/10 p-3';
export const ACCESS_LABEL = 'text-forest-on-dark font-mono text-[10px] uppercase tracking-[0.16em]';

/**
 * WLAN auf dem tiefgrünen Grund: Netz, Passwort zum Kopieren und der QR-Code zum Aufklappen —
 * dieselben Angaben wie im alten WLAN-Aufklapper unter „Ankommen“.
 */
export function WifiTile({ ssid, password }: { ssid: string | null; password: string | null }) {
  const { t } = useTranslation();
  const [copied, copy, copyFailed] = useCopyToClipboard();
  const [showQr, setShowQr] = useState(false);
  const qrId = useId();
  const qrValue = ssid && password ? buildWifiUri(ssid, password) : null;

  return (
    <div className={ACCESS_TILE}>
      <div className="flex flex-wrap items-center gap-2.5">
        <Wifi className="text-forest-on-dark size-5 shrink-0" aria-hidden />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className={ACCESS_LABEL}>{t('stations.stay.wifi.title')}</span>
          {/* Umbrechen statt kürzen: den Namen sucht der Gast in seiner WLAN-Liste. */}
          <span className="text-[15px] font-bold text-white [overflow-wrap:anywhere]">
            {ssid ?? '—'}
          </span>
        </span>
        {ssid && <CopyIconButton value={ssid} label={t('travel.access.wifiName')} tone="dark" />}
        {qrValue && (
          <button
            type="button"
            onClick={() => setShowQr((value) => !value)}
            aria-expanded={showQr}
            aria-controls={qrId}
            aria-label={t(showQr ? 'travel.access.hideQr' : 'travel.access.showQr')}
            className="flex size-11 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
          >
            <QrCode className="size-4" aria-hidden />
          </button>
        )}
      </div>
      {password && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <span className="min-w-0 break-all font-mono text-sm tracking-wide text-white/85">
            {password}
          </span>
          <button
            type="button"
            onClick={() => void copy(password)}
            className="bg-bg-elev text-fg min-h-11 shrink-0 rounded-full px-4 text-[13px] font-bold"
          >
            {copyFailed
              ? t('travel.copy.failedShort')
              : copied
                ? t('stations.stay.wifi.copied')
                : t('travel.access.copyPassword')}
          </button>
        </div>
      )}
      {qrValue && (
        <div id={qrId} hidden={!showQr} className="mt-3 flex flex-col items-center gap-2">
          {/* QR-Scanner brauchen echtes Weiß — bewusst gegen die Markenfarbe. */}
          <div className="rounded-guest-cell bg-white p-2">
            <QRCodeSVG value={qrValue} size={140} level="M" />
          </div>
          <p className={ACCESS_LABEL}>{t('stations.stay.wifi.scanHint')}</p>
        </div>
      )}
    </div>
  );
}

// WPA-WLAN-QR nach https://en.wikipedia.org/wiki/QR_code#WiFi_login — Sonderzeichen (\ ; , : ")
// müssen in Netz und Passwort mit Backslash maskiert werden.
function buildWifiUri(ssid: string, password: string): string {
  const esc = (s: string) => s.replace(/([\\;,":])/g, '\\$1');
  return `WIFI:T:WPA;S:${esc(ssid)};P:${esc(password)};;`;
}
